// ProductImageGeneratorFrontendOnly.tsx
import React, { useState, useMemo, useEffect } from 'react';
import './MediaLibraryPopup.scss';
import { closeIcon, searchIcon } from '../../utils/images';
import { useMediaLibrary } from '../../contexts/MediaLibraryContext';

interface MediaLibraryPopupProps {
  onClose: () => void;
  onMediaSelect?: (mediaUrl: string) => void;
  isUploading?: boolean;
}

export const MediaLibraryPopup: React.FC<MediaLibraryPopupProps> = ({ 
  onClose, 
  onMediaSelect,
  isUploading = false
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [authenticatedImages, setAuthenticatedImages] = useState<{[key: string]: string}>({});
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const { mediaLibraryData, loading, error, refreshMediaLibrary, getAuthenticatedImage } = useMediaLibrary();

  const filteredMedia = useMemo(() => {
    return mediaLibraryData.images.filter(mediaImage => {
      const query = searchQuery.toLowerCase();
      const nameMatch = mediaImage.name.toLowerCase().includes(query);
      return nameMatch;
    });
  }, [mediaLibraryData.images, searchQuery]);

  // Load authenticated images
  useEffect(() => {
    const loadAuthenticatedImages = async () => {
      if (filteredMedia.length === 0) return;

      const newLoadingImages = new Set(filteredMedia.map(img => img.url));
      setLoadingImages(newLoadingImages);

      const imagePromises = filteredMedia.map(async (mediaImage) => {
        try {
          const authenticatedUrl = await getAuthenticatedImage(mediaImage.url);
          return { originalUrl: mediaImage.url, authenticatedUrl };
        } catch (error) {
          console.error(`Error loading image ${mediaImage.name}:`, error);
          return null;
        }
      });

      const results = await Promise.all(imagePromises);
      const imageMap: {[key: string]: string} = {};
      
      results.forEach(result => {
        if (result) {
          imageMap[result.originalUrl] = result.authenticatedUrl;
        }
      });

      setAuthenticatedImages(prev => ({ ...prev, ...imageMap }));
      setLoadingImages(new Set());
    };

    loadAuthenticatedImages();
  }, [filteredMedia, getAuthenticatedImage]);

  const handleMediaClick = (mediaUrl: string) => {
    if (isUploading) return; // Prevent clicks during upload
    
    if (onMediaSelect) {
      onMediaSelect(mediaUrl);
      onClose();
    }
  };

  if (error) {
    return (
      <div className="media-popup">
        <div className="media-popup__header">
          <div className="media-popup__close" onClick={onClose}>
            <img className="media-popup__close--icon" src={closeIcon} alt="close" />
          </div>
          <h1 className="media-popup__title">Media Library</h1>
        </div>
        <div className="media-popup__container">
          <div className="media-popup__error">
            <p>Error: {error}</p>
            <button onClick={refreshMediaLibrary}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="media-popup">
      <div className="media-popup__header">
        <div className="media-popup__close" onClick={onClose}>
          <img className="media-popup__close--icon" src={closeIcon} alt="close" />
        </div>
        <h1 className="media-popup__title">Media Library</h1>
      </div>

      <div className="media-popup__container">
        <div className="media-popup__search">
          <img className="media-popup__search--icon" src={searchIcon} alt='search icon'/>
          <input 
            type="text" 
            className="media-popup__search--input" 
            placeholder="Search media files"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {loading && mediaLibraryData.images.length === 0 ? (
          <div className="media-popup__loading">Loading media...</div>
        ) : (
          <div className="media-popup__list">
            {isUploading && (
              <div className="media-popup__upload-overlay">
                <div className="media-popup__upload-message">Uploading image to display...</div>
              </div>
            )}
            {filteredMedia.length > 0 ? (
              filteredMedia.map((mediaImage, index) => (
                <div
                  key={`${mediaImage.name}-${index}`}
                  className={`media-popup__item ${isUploading ? 'media-popup__item--disabled' : ''}`}
                  onClick={() => handleMediaClick(mediaImage.url)}
                >
                  {loadingImages.has(mediaImage.url) ? (
                    <div className="media-popup__image-loading">Loading...</div>
                  ) : authenticatedImages[mediaImage.url] ? (
                    <img 
                      src={authenticatedImages[mediaImage.url]} 
                      alt={mediaImage.name}
                      className="media-popup__image"
                    />
                  ) : (
                    <div className="media-popup__image-error">Failed to load</div>
                  )}
                </div>
              ))
            ) : (
              <div className="media-popup__no-results">
                No media files found matching your search.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};